import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, InputNumber, Select, Button, Space, Table, Tag, Alert,
  Divider, Typography, Progress, Drawer, Descriptions, Switch,
} from '@/shared/ui/primitives';
import { CloudDownloadOutlined, ReloadOutlined, EyeOutlined, DeleteOutlined, UploadOutlined } from '@/shared/ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import { UserSelect } from '@/shared/ui/entity-selects';
import {
  importApi, type AssignmentMode, type ImportItem, type ImportRun,
} from '../api/import.api';
import { formatDateTime } from '@/shared/utils/formatters';

const OUTCOME_COLOR: Record<ImportItem['outcome'], string> = {
  IMPORTED: 'green',
  DUPLICATE: 'default',
  FAILED: 'red',
};

const USER_ASSIGNMENT_COLOR: Record<string, string> = {
  ASSIGNED_TO_OWNER: 'blue',
  MATCHED_BY_PHONE: 'green',
  CREATED_FROM_PROFILE: 'purple',
  UNRESOLVED: 'red',
};

const USER_ASSIGNMENT_LABEL: Record<string, string> = {
  ASSIGNED_TO_OWNER: 'Owner',
  MATCHED_BY_PHONE: 'Matched',
  CREATED_FROM_PROFILE: 'Created',
  UNRESOLVED: 'Unresolved',
};

const RUN_STATUS_COLOR: Record<ImportRun['status'], string> = {
  RUNNING: 'processing',
  SUCCESS: 'success',
  PARTIAL: 'warning',
  FAILED: 'error',
};

function ModeCard({
  active, title, description, badge, onClick,
}: { active: boolean; title: string; description: string; badge?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ds-card"
      style={{
        flex: '1 1 280px',
        textAlign: 'start',
        padding: 16,
        border: active ? '2px solid var(--color-primary)' : '1px solid var(--grey-200)',
        background: active ? 'var(--color-primary-soft)' : 'var(--bg-card)',
        boxShadow: active ? '0 0 0 4px rgba(255,179,102,.12)' : 'var(--shadow-xs)',
        cursor: 'pointer',
        transition: 'all .15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
        {badge && <Tag color="primary">{badge}</Tag>}
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}>{description}</div>
    </button>
  );
}

function adLink(item: ImportItem) {
  const wantedId = item.createdRoomWantedAdId ?? item.existingRoomWantedAdId;
  if (wantedId) {
    return (
      <Link to={`/room-wanted/${wantedId}/edit`}>
        #{wantedId}
        {item.outcome === 'DUPLICATE' && ' (existing)'}
      </Link>
    );
  }
  // Legacy rows that went through the old room-ad importer
  const adId = item.createdAdId ?? item.existingAdId;
  if (adId) return <Link to={`/room-ads/${adId}/edit`}>#{adId}</Link>;
  return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
}

function UserCell({ item }: { item: ImportItem }) {
  if (!item.userAssignment) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const color = USER_ASSIGNMENT_COLOR[item.userAssignment] ?? 'default';
  const label = USER_ASSIGNMENT_LABEL[item.userAssignment] ?? item.userAssignment;
  return (
    <Space size="small" style={{ whiteSpace: 'nowrap' }}>
      <Tag color={color}>{label}</Tag>
      {item.assignedUserId && <Link to={`/users/${item.assignedUserId}`}>#{item.assignedUserId}</Link>}
    </Space>
  );
}

// The list of cities that Roomaty can be scraped for is owned by the
// backend (see RoomatyImportService.supportedCityIds). The dashboard fetches
// it via /admin/imports/roomaty/supported-cities and never sees Roomaty's
// path/slug values directly.

export default function RoomatyImportPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const supportedCitiesQuery = useQuery({
    queryKey: ['import', 'roomaty', 'supported-cities'],
    queryFn: () => importApi.roomatySupportedCities(),
    staleTime: 5 * 60_000,
  });

  const supportedCityIds = useMemo(
    () => new Set<number>(supportedCitiesQuery.data?.cityIds ?? []),
    [supportedCitiesQuery.data],
  );

  const cityOptions = useMemo(() => {
    const cities = supportedCitiesQuery.data?.cities ?? [];
    return cities.map((c) => ({
      value: c.id,
      label: c.nameEn + (c.nameAr ? ` — ${c.nameAr}` : ''),
    }));
  }, [supportedCitiesQuery.data]);

  const [cityId, setCityId] = useState<number | null>(null);
  const cityNotSupported = cityId != null && supportedCityIds.size > 0 && !supportedCityIds.has(cityId);
  const [maxAds, setMaxAds] = useState<number | null>(null);
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('MATCH_OR_CREATE');
  const [ownerUserId, setOwnerUserId] = useState<number | null>(null);
  const [status, setStatus] = useState<string>('DRAFT');

  // Visibility flags
  const [featured, setFeatured] = useState(false);
  const [showPhone, setShowPhone] = useState(true);
  const [showWhatsApp, setShowWhatsApp] = useState(true);
  const [showOwnerInfo, setShowOwnerInfo] = useState(false);
  // In-app messaging defaults OFF for admin imports — scraped users typically
  // shouldn't auto-opt-in to the chat feature without an explicit admin nod.
  const [showInAppMessaging, setShowInAppMessaging] = useState(false);
  const [hideLocation, setHideLocation] = useState(false);

  // Image upload
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageErrors, setImageErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
  const ALLOWED_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']);

  const addImages = useCallback((files: FileList | null) => {
    if (!files) return;
    const errs: string[] = [];
    const valid: File[] = [];
    Array.from(files).forEach((f) => {
      if (!ALLOWED_TYPES.has(f.type)) {
        errs.push(t('import.roomaty.imagesErrorType') + `: ${f.name}`);
      } else if (f.size > MAX_IMAGE_BYTES) {
        errs.push(t('import.roomaty.imagesErrorSize') + `: ${f.name}`);
      } else {
        valid.push(f);
      }
    });
    setImageErrors(errs);
    setImageFiles((prev) => [...prev, ...valid]);
  }, [t]);

  const removeImage = useCallback((idx: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const [lastRun, setLastRun] = useState<ImportRun | null>(null);
  const [drawerRunId, setDrawerRunId] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const runMutation = useMutation({
    mutationFn: ({ body, images }: { body: any; images: File[] }) => importApi.runRoomaty(body, images),
    onSuccess: (data) => {
      setLastRun(data);
      qc.invalidateQueries({ queryKey: ['import', 'runs'] });
      toast.success(
        t('import.toasts.finished', {
          imported: data.imported,
          skipped: data.skipped,
          failed: data.failed,
        }),
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('import.toasts.failed'));
    },
  });

  const runsQuery = useQuery({
    queryKey: ['import', 'runs', { page: 0, size: 20, source: 'ROOMATY' }],
    queryFn: () => importApi.listRuns({ page: 0, size: 20, source: 'ROOMATY' }),
    refetchInterval: 30_000,
  });

  const drawerQuery = useQuery({
    queryKey: ['import', 'runs', drawerRunId],
    queryFn: () => importApi.getRun(drawerRunId!),
    enabled: !!drawerRunId,
  });

  const ownerMissing = assignmentMode === 'ASSIGN_TO_USER' && ownerUserId == null;
  const disabled = runMutation.isPending || ownerMissing || cityId == null || cityNotSupported;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (ownerMissing || cityId == null) return;
    runMutation.mutate({
      body: {
        cityId,
        maxAds,
        assignmentMode,
        ownerUserId: assignmentMode === 'ASSIGN_TO_USER' ? ownerUserId : null,
        status,
        featured,
        showPhone,
        showWhatsApp,
        showOwnerInfo,
        showInAppMessaging,
        hideLocation,
      },
      images: imageFiles,
    });
  };

  const runHistory = useMemo(() => (runsQuery.data as any)?.content ?? [], [runsQuery.data]);

  return (
    <div>
      <PageHeader
        title={t('import.roomaty.title')}
        subtitle={t('import.roomaty.subtitle')}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('import.roomaty.howItWorksTitle')}
        description={t('import.roomaty.howItWorks')}
      />

      <Card title={t('import.roomaty.triggerTitle')}>
        <form onSubmit={onSubmit} className="ds-form">

          {/* ── Source ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div className="ds-form-item" data-testid="import-city-select">
              <label className="ds-form-label required">
                {t('import.roomaty.cityLabel', { defaultValue: 'City' })}
              </label>
              <Select
                value={cityId ?? undefined}
                onChange={(v: any) => setCityId(v ?? null)}
                options={cityOptions}
                loading={supportedCitiesQuery.isLoading}
                showSearch
                allowClear={false}
                placeholder={t('import.roomaty.cityPlaceholder', { defaultValue: 'Select a city to import' })}
                filterOption={(input: string, option: any) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
              <div className="ds-form-hint" data-testid="import-city-path-preview">
                {cityId == null
                  ? t('import.roomaty.cityPickHint', {
                      defaultValue: 'Pick the city whose ads you want to import.',
                    })
                  : cityNotSupported
                  ? t('import.roomaty.cityNotSupported', {
                      defaultValue: 'Roomaty import is not supported for this city yet.',
                    })
                  : t('import.roomaty.cityReady', {
                      defaultValue: 'Ready — this city is supported.',
                    })}
              </div>
            </div>

            <div className="ds-form-item">
              <label className="ds-form-label">{t('import.roomaty.maxAds')}</label>
              <InputNumber value={maxAds ?? undefined} onChange={(v: any) => setMaxAds(v ? Number(v) : null)} min={1} placeholder={t('import.roomaty.maxAdsUnlimited')} style={{ width: '100%' }} />
            </div>

            <div className="ds-form-item">
              <label className="ds-form-label">{t('import.roomaty.status')}</label>
              <Select
                value={status}
                onChange={(v: any) => setStatus(v)}
                options={[
                  { value: 'DRAFT', label: t('roomAds.DRAFT') },
                  { value: 'PUBLISHED', label: t('roomAds.PUBLISHED') },
                ]}
              />
              <div className="ds-form-hint">{t('import.roomaty.statusHint')}</div>
            </div>
          </div>

          <Divider />

          {/* ── Assignment mode ── */}
          <div className="ds-form-item">
            <label className="ds-form-label required">{t('import.roomaty.assignmentMode')}</label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <ModeCard
                active={assignmentMode === 'ASSIGN_TO_USER'}
                title={t('import.roomaty.mode.assignTitle')}
                description={t('import.roomaty.mode.assignDesc')}
                onClick={() => setAssignmentMode('ASSIGN_TO_USER')}
              />
              <ModeCard
                active={assignmentMode === 'MATCH_OR_CREATE'}
                title={t('import.roomaty.mode.matchTitle')}
                description={t('import.roomaty.mode.matchDesc')}
                badge={t('import.roomaty.mode.recommended')}
                onClick={() => setAssignmentMode('MATCH_OR_CREATE')}
              />
            </div>
          </div>

          {assignmentMode === 'ASSIGN_TO_USER' && (
            <div className="ds-form-item">
              <label className="ds-form-label required">{t('import.roomaty.ownerUser')}</label>
              <UserSelect
                value={ownerUserId}
                onChange={setOwnerUserId}
                placeholder={t('import.roomaty.ownerUserPlaceholder')}
                status={submitted && ownerMissing ? 'error' : undefined}
              />
              {submitted && ownerMissing && (
                <div className="ds-form-error">{t('import.roomaty.ownerRequired')}</div>
              )}
              <div className="ds-form-hint">{t('import.roomaty.ownerHint')}</div>
            </div>
          )}

          {assignmentMode === 'MATCH_OR_CREATE' && (
            <Alert
              type="warning"
              showIcon
              message={t('import.roomaty.mode.matchNoticeTitle')}
              description={t('import.roomaty.mode.matchNoticeDesc')}
            />
          )}

          <Divider />

          {/* ── Image upload ── */}
          <div className="ds-form-item">
            <label className="ds-form-label">{t('import.roomaty.imagesTitle')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                {t('import.roomaty.imagesHint')}
              </div>
              <Button
                icon={<UploadOutlined />}
                onClick={() => fileInputRef.current?.click()}
                disabled={runMutation.isPending}
              >
                {t('import.roomaty.imagesAdd')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => { addImages(e.target.files); e.target.value = ''; }}
              />
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {t('import.roomaty.imagesDropHint')}
              </div>
              {imageErrors.map((err, i) => (
                <div key={i} style={{ color: 'var(--error)', fontSize: 12 }}>{err}</div>
              ))}
              {imageFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {imageFiles.map((f, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'relative',
                        width: 80, height: 80,
                        border: '1px solid var(--grey-200)',
                        borderRadius: 6,
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={URL.createObjectURL(f)}
                        alt={f.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        style={{
                          position: 'absolute', top: 2, right: 2,
                          background: 'rgba(0,0,0,.55)', border: 'none',
                          borderRadius: 4, padding: '2px 4px', cursor: 'pointer',
                          color: '#fff', lineHeight: 1,
                        }}
                        title={t('import.roomaty.imagesRemove')}
                      >
                        <DeleteOutlined style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Divider />

          {/* ── Visibility & Contact flags ── */}
          <div className="ds-form-item">
            <label className="ds-form-label">{t('import.roomaty.visibilityTitle')}</label>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
              {t('import.roomaty.visibilityHint')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              {([
                { key: 'featured', label: t('import.roomaty.flagFeatured'), desc: t('import.roomaty.flagFeaturedDesc'), value: featured, set: setFeatured },
                { key: 'showPhone', label: t('import.roomaty.flagShowPhone'), desc: t('import.roomaty.flagShowPhoneDesc'), value: showPhone, set: setShowPhone },
                { key: 'showWhatsApp', label: t('import.roomaty.flagShowWhatsApp'), desc: t('import.roomaty.flagShowWhatsAppDesc'), value: showWhatsApp, set: setShowWhatsApp },
                { key: 'showOwnerInfo', label: t('import.roomaty.flagShowOwnerInfo'), desc: t('import.roomaty.flagShowOwnerInfoDesc'), value: showOwnerInfo, set: setShowOwnerInfo },
                { key: 'showInAppMessaging', label: t('import.roomaty.flagShowInAppMessaging'), desc: t('import.roomaty.flagShowInAppMessagingDesc'), value: showInAppMessaging, set: setShowInAppMessaging },
                { key: 'hideLocation', label: t('import.roomaty.flagHideLocation'), desc: t('import.roomaty.flagHideLocationDesc'), value: hideLocation, set: setHideLocation },
              ] as const).map(({ key, label, desc, value, set }) => (
                <div
                  key={key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: 12,
                    border: '1px solid var(--grey-200)',
                    borderRadius: 8,
                    background: 'var(--bg-card)',
                  }}
                >
                  <Switch checked={value} onChange={(v: boolean) => (set as (v: boolean) => void)(v)} disabled={runMutation.isPending} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<CloudDownloadOutlined />}
                loading={runMutation.isPending}
                disabled={disabled}
              >
                {t('import.roomaty.runButton')}
              </Button>
              <Button
                onClick={() => {
                  setCityId(null);
                  setMaxAds(null);
                  setAssignmentMode('MATCH_OR_CREATE');
                  setOwnerUserId(null);
                  setStatus('DRAFT');
                  setFeatured(false);
                  setShowPhone(true);
                  setShowWhatsApp(true);
                  setShowOwnerInfo(true);
                  setShowInAppMessaging(false);
                  setHideLocation(false);
                  setImageFiles([]);
                  setImageErrors([]);
                  setSubmitted(false);
                }}
                disabled={runMutation.isPending}
              >
                {t('common.reset')}
              </Button>
            </Space>
            {runMutation.isPending && (
              <div style={{ marginTop: 12 }}>
                <Progress percent={100} status="active" showInfo={false} />
                <Typography.Text type="secondary">{t('import.roomaty.running')}</Typography.Text>
              </div>
            )}
          </div>
        </form>
      </Card>

      {lastRun && <LastRunCard run={lastRun} />}

      <Card
        title={
          <Space>
            {t('import.roomaty.historyTitle')}
            <Button size="small" icon={<ReloadOutlined />} onClick={() => runsQuery.refetch()} />
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        <Table
          size="small"
          rowKey="runId"
          loading={runsQuery.isLoading}
          dataSource={runHistory}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          columns={[
            { title: '#', dataIndex: 'runId', width: 60 },
            {
              title: t('common.status'),
              dataIndex: 'status',
              width: 110,
              render: (v: ImportRun['status']) => <Tag color={RUN_STATUS_COLOR[v]}>{v}</Tag>,
            },
            { title: t('import.columns.admin'), dataIndex: 'triggeredByAdminEmail', width: 220, render: (v: string) => v || <Tag>system</Tag> },
            { title: t('import.roomaty.scanned'), dataIndex: 'scanned', width: 80, align: 'center' as const },
            { title: t('import.roomaty.imported'), dataIndex: 'imported', width: 80, align: 'center' as const },
            { title: t('import.roomaty.skipped'), dataIndex: 'skipped', width: 80, align: 'center' as const },
            { title: t('import.roomaty.failed'), dataIndex: 'failed', width: 80, align: 'center' as const },
            { title: t('import.roomaty.usersCreated'), dataIndex: 'usersCreated', width: 100, align: 'center' as const },
            { title: t('import.roomaty.usersMatched'), dataIndex: 'usersMatched', width: 100, align: 'center' as const },
            { title: t('common.createdAt'), dataIndex: 'createdAt', width: 180, render: (v: string) => formatDateTime(v) },
            {
              title: '',
              width: 70,
              render: (_: any, r: ImportRun) => (
                <Button type="link" icon={<EyeOutlined />} onClick={() => setDrawerRunId(r.runId)} />
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        width={820}
        open={drawerRunId !== null}
        onClose={() => setDrawerRunId(null)}
        title={drawerRunId ? `${t('import.roomaty.historyTitle')} · #${drawerRunId}` : ''}
      >
        {drawerQuery.data && (
          <>
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('common.status')}>
                <Tag color={RUN_STATUS_COLOR[drawerQuery.data.status]}>{drawerQuery.data.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('import.columns.admin')}>{drawerQuery.data.triggeredByAdminEmail || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('common.createdAt')}>{formatDateTime(drawerQuery.data.createdAt)}</Descriptions.Item>
              <Descriptions.Item label={t('import.roomaty.finishedAt')}>{formatDateTime(drawerQuery.data.finishedAt)}</Descriptions.Item>
              <Descriptions.Item label={t('import.roomaty.summary')}>{drawerQuery.data.summary}</Descriptions.Item>
            </Descriptions>
            <ItemsTable items={drawerQuery.data.items} />
          </>
        )}
      </Drawer>
    </div>
  );
}

function LastRunCard({ run }: { run: ImportRun }) {
  const { t } = useTranslation();
  return (
    <Card title={t('import.roomaty.lastRunTitle')} style={{ marginTop: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
        <SummaryTile label={t('import.roomaty.scanned')} value={run.scanned} />
        <SummaryTile label={t('import.roomaty.imported')} value={run.imported} tone="success" />
        <SummaryTile label={t('import.roomaty.skipped')} value={run.skipped} tone="neutral" />
        <SummaryTile label={t('import.roomaty.failed')} value={run.failed} tone={run.failed ? 'danger' : 'neutral'} />
        <SummaryTile label={t('import.roomaty.usersCreated')} value={run.usersCreated} tone="primary" />
        <SummaryTile label={t('import.roomaty.usersMatched')} value={run.usersMatched} tone="info" />
        {run.missingPhoneCount > 0 && <SummaryTile label={t('import.roomaty.missingPhone')} value={run.missingPhoneCount} tone="danger" />}
        {run.unmappedValueCount > 0 && <SummaryTile label={t('import.roomaty.unmapped')} value={run.unmappedValueCount} tone="neutral" />}
        {run.imageFailureCount > 0 && <SummaryTile label={t('import.roomaty.imageFailures')} value={run.imageFailureCount} tone="danger" />}
        {run.detailPagesFetched > 0 && <SummaryTile label={t('import.roomaty.detailFetched')} value={run.detailPagesFetched} tone="info" />}
        {run.recoveredPhoneCount > 0 && <SummaryTile label={t('import.roomaty.recoveredPhone')} value={run.recoveredPhoneCount} tone="success" />}
        {run.authFailureCount > 0 && <SummaryTile label={t('import.roomaty.authFailures')} value={run.authFailureCount} tone="danger" />}
      </div>
      <Space style={{ marginBottom: 12 }}>
        <Tag color={RUN_STATUS_COLOR[run.status]}>{run.status}</Tag>
        <span style={{ color: 'var(--text-tertiary)' }}>
          {t('import.roomaty.runId')}: #{run.runId} · {formatDateTime(run.createdAt)} → {formatDateTime(run.finishedAt)}
        </span>
      </Space>
      <Divider style={{ margin: '8px 0 12px' }} />
      <ItemsTable items={run.items} />
    </Card>
  );
}

function SummaryTile({ label, value, tone = 'default' }: { label: string; value: number | string; tone?: 'success' | 'danger' | 'neutral' | 'primary' | 'info' | 'default' }) {
  const color =
    tone === 'success' ? 'var(--success)' :
    tone === 'danger'  ? 'var(--error)' :
    tone === 'primary' ? 'var(--color-primary-dark)' :
    tone === 'info'    ? 'var(--info)' :
    tone === 'neutral' ? 'var(--text-tertiary)' : 'var(--text-primary)';
  return (
    <div className="ds-stat" style={{ padding: 14 }}>
      <div className="ds-stat-head" style={{ fontSize: 12 }}>{label}</div>
      <div className="ds-stat-value" style={{ color, fontSize: 24 }}>{value}</div>
    </div>
  );
}

function ItemsTable({ items }: { items: ImportItem[] }) {
  const { t } = useTranslation();
  return (
    <Table
      size="small"
      rowKey="id"
      dataSource={items}
      pagination={false}
      columns={[
        {
          title: t('import.columns.outcome'),
          dataIndex: 'outcome',
          width: 110,
          render: (v: ImportItem['outcome']) => <Tag color={OUTCOME_COLOR[v]}>{v}</Tag>,
        },
        {
          title: t('import.columns.source'),
          width: 150,
          render: (_: any, r: ImportItem) =>
            r.sourceUrl
              ? <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer">#{r.sourceId}</a>
              : r.sourceId ?? '—',
        },
        { title: t('import.columns.title'), dataIndex: 'title', ellipsis: true },
        {
          title: t('import.columns.phone'),
          dataIndex: 'normalizedPhone',
          width: 150,
          render: (v: string) => v
            ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{v}</span>
            : <span style={{ color: 'var(--text-tertiary)' }}>—</span>,
        },
        {
          title: t('import.columns.user'),
          width: 180,
          render: (_: any, r: ImportItem) => <UserCell item={r} />,
        },
        {
          title: t('import.columns.wantedAd'),
          width: 130,
          render: (_: any, r: ImportItem) => adLink(r),
        },
        { title: t('import.columns.images'), dataIndex: 'imagesUploaded', width: 90, align: 'center' as const },
        { title: t('import.columns.message'), dataIndex: 'message', ellipsis: true },
      ]}
    />
  );
}
